import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { posZeroLoadingSelector, userSelector } from 'app-redux/selectors'
import { flushQueue } from 'utils/posLocalDb/asyncUploader'
import { setQueueUserId } from 'utils/posLocalDb/queueContext'
import { useAsyncQueueStatus } from 'utils/posLocalDb/queueVersion'

/**
 * Interval flush periodik — jaring pengaman retry & konfirmasi op 'sent'. Retry
 * sebenarnya dijadwalkan per-op dengan backoff, jadi flush tanpa op jatuh tempo
 * berhenti tanpa hit API.
 */
const FLUSH_INTERVAL_MS = 60_000

/**
 * Tick lokal selama masih ada op menunggu (pending/sent) supaya "Terkirim" cepat
 * menjadi "Berhasil". Frekuensi GET ke server tetap dibatasi backoff per batch
 * (4s→8s→…→60s); berhenti sendiri saat tak ada op in-flight.
 */
const INFLIGHT_POLL_MS = 6_000

/**
 * Mendorong antrean transaksi async ke server di latar belakang (Mode Zero-Loading):
 * saat mount, tab kembali aktif, koneksi pulih, dan periodik. Juga menyetel pemilik
 * antrean (user login) agar enqueue/flush di-scope ke kasir aktif.
 */
const usePosAsyncQueueSync = () => {
  const enabled = useSelector(posZeroLoadingSelector)
  const user = useSelector(userSelector)
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  // Disetel saat render (bukan effect): effect komponen anak (badge, riwayat) jalan
  // lebih dulu dari effect layout, jadi pemilik antrean harus sudah ada saat itu.
  setQueueUserId(user?.id ?? null)

  const { pending, sent } = useAsyncQueueStatus(enabled)
  const inFlight = pending + sent > 0

  const flush = useCallback(() => {
    if (enabledRef.current) {
      flushQueue()
    }
  }, [])

  useEffect(() => {
    if (enabled && user?.id) {
      flush()
    }
  }, [enabled, user?.id, flush])

  useEffect(() => {
    if (!enabled) {
      return undefined
    }
    const id = setInterval(flush, FLUSH_INTERVAL_MS)
    return () => clearInterval(id)
  }, [enabled, flush])

  useEffect(() => {
    if (!enabled || !inFlight) {
      return undefined
    }
    const id = setInterval(flush, INFLIGHT_POLL_MS)
    return () => clearInterval(id)
  }, [enabled, inFlight, flush])

  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        flush()
      }
    }
    document.addEventListener('visibilitychange', handleVisible)
    window.addEventListener('online', flush)
    return () => {
      document.removeEventListener('visibilitychange', handleVisible)
      window.removeEventListener('online', flush)
    }
  }, [flush])

  return { flush }
}

export default usePosAsyncQueueSync
